import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  AggregatorAreaFilter,
  type AggregatorAreaOption,
  buildAggregatorAreaOptions,
} from "@/components/AggregatorAreaFilter";
import { FilterBar, FilterDefinition, FilterOption } from "@/components/FilterBar";

import { QUALITIES } from "../../../constants";
import { getLocalisedName, translateOptions } from "../../../utils/i18n";
import { DepositFilterValues } from "../types";
import { DEFAULT_STOCK_STATUS, STOCK_STATUS_OPTIONS } from "../utils";

interface FiltersProps {
  commodities: { id: number; name: string }[];
  aggregators: AggregatorAreaOption[];
  filters: DepositFilterValues;
  onChange: (patch: Partial<DepositFilterValues>) => void;
  onClear: () => void;
}

export const Filters = ({ commodities, aggregators, filters, onChange, onClear }: FiltersProps) => {
  const { t } = useTranslation();

  const aggregatorOptions = useMemo(
    () => buildAggregatorAreaOptions(aggregators || []),
    [aggregators]
  );

  const definitions: FilterDefinition<keyof DepositFilterValues>[] = [
    {
      key: "stock_status",
      label: t("filters.stockStatus"),
      includeAllOption: false,
      defaultValue: DEFAULT_STOCK_STATUS,
      options: STOCK_STATUS_OPTIONS.map(option => ({
        value: option,
        label: t(`farmersMarket.stockStatus.${option}`),
      })),
    },
    {
      key: "aggregator_ids",
      label: t("filters.aggregatorArea"),
      options: [],
      render: id => (
        <AggregatorAreaFilter
          id={id}
          options={aggregatorOptions}
          value={filters.aggregator_ids}
          onChange={aggregator_ids => onChange({ aggregator_ids })}
        />
      ),
    },
    {
      key: "commodity_id",
      label: t("filters.commodity"),
      searchable: true,
      options: (commodities || []).map(commodity => ({
        value: commodity.id.toString(),
        label: getLocalisedName(commodity),
      })),
    },
    {
      key: "quality",
      label: t("filters.quality"),
      options: translateOptions(QUALITIES, t).map((option: FilterOption) => ({
        value: option.value,
        label: option.label,
      })),
    },
  ];

  return (
    <FilterBar definitions={definitions} values={filters} onChange={onChange} onClear={onClear} />
  );
};
